import { useState, useEffect } from "react";
import { GUEST_CLAIM_KEY } from "../components/studio/constants";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { BrandLockup } from "../components/BrandLogo";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EmailCodeSignIn } from "../components/auth/EmailCode";

function handleGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function errText(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function SignIn() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [pendingJd, setPendingJd] = useState(false);
  const [fromCheck] = useState(() => Boolean(localStorage.getItem(GUEST_CLAIM_KEY)));
  const [showEmail, setShowEmail] = useState(false);
  const [isRegister, setIsRegister] = useState(true);
  const [codeMode, setCodeMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState({});

  useEffect(() => {
    setPendingJd(!!(localStorage.getItem("pending_jd") || localStorage.getItem("pending_url")));
    api.get("/auth/providers").then(({ data }) => setProviders(data)).catch(() => {});
    const err = new URLSearchParams(window.location.search).get("error");
    const name = "LinkedIn";
    if (err === "linkedin") toast.error(`${name} sign-in didn't complete. Please try again, or use Google or email.`, { id: "oauth-error" });
    if (err?.endsWith("_not_configured")) toast.info(`${name} sign-in is being set up. For now, use Google or email.`, { id: "oauth-error" });
    if (err) navigate("/signin", { replace: true });
  }, [navigate]);

  const startOAuth = (provider) => {
    if (!providers[provider]) return notReady("LinkedIn");
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/${provider}/login`;
  };

  const submitEmail = async () => {
    if (!form.email.trim() || !form.password) return toast.error("Enter your email and password");
    setSubmitting(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      await api.post(path, form);
      await checkAuth();
      navigate("/dashboard");
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const notReady = (name) =>
    toast.info(`${name} sign-in is being set up. For now, use Google or email — it takes seconds.`);

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      <header className="h-16 flex items-center justify-between px-5 sm:px-6 border-b border-line bg-surface">
        <a href="/" data-testid="home-logo-link" className="flex items-center group" aria-label="GetInterviewed home">
          <BrandLockup />
        </a>
        <button data-testid="signin-back-button" onClick={() => navigate("/")} className="gi-btn gi-btn-secondary gi-btn-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md af-fade-up" data-testid="signin-page">
          <h1 className="font-display text-3xl font-bold tracking-tight text-center text-ink">Sign in or create your account</h1>
          <p className="text-body text-center mt-2">One account for your CV, applications and Career Journey.</p>

          {fromCheck && (
            <div className="mt-6 rounded-xl border border-brand/30 bg-brand-soft p-4 flex items-center gap-3" data-testid="free-check-claim-note">
              <Sparkles className="w-5 h-5 text-brand shrink-0" />
              <span className="text-sm text-body">Sign in or create a free account and we'll bring over the CV from your free check.</span>
            </div>
          )}
          {pendingJd && !fromCheck && (
            <div className="mt-6 rounded-xl border border-brand/30 bg-brand-soft p-4 flex items-center gap-3" data-testid="pending-jd-note">
              <Sparkles className="w-5 h-5 text-brand shrink-0" />
              <span className="text-sm text-body">Nice — we've saved that job. Sign in and we'll tailor your CV to it straight away.</span>
            </div>
          )}

          <div className="mt-8 space-y-3">
            <button
              data-testid="google-login-button"
              onClick={handleGoogleLogin}
              className="w-full gi-btn gi-btn-lg gi-btn-secondary"
            >
              <GoogleGlyph /> Continue with Google
            </button>

            <button
              data-testid="linkedin-login-button"
              onClick={() => startOAuth("linkedin")}
              className="w-full gi-btn gi-btn-lg bg-[#0A66C2] text-white hover:bg-[#095aac]"
            >
              <LinkedInGlyph /> Continue with LinkedIn
              {!providers.linkedin && <span className="text-xs font-mono2 uppercase tracking-widest opacity-70">soon</span>}
            </button>

            <button
              data-testid="email-login-button"
              onClick={() => setShowEmail((v) => !v)}
              aria-expanded={showEmail} className="w-full gi-btn gi-btn-lg gi-btn-secondary"
            >
              <Mail className="w-5 h-5" /> Continue with email
            </button>

            {showEmail && (
              <div className="gi-card p-5 space-y-4" data-testid="email-auth-form">
                <div className="flex rounded-xl bg-subtle p-1 text-sm" role="tablist" aria-label="Account">
                  <button data-testid="email-mode-signup" onClick={() => { setIsRegister(true); setCodeMode(false); }} role="tab" aria-selected={isRegister && !codeMode} className={`flex-1 py-2 min-h-[36px] rounded-lg font-semibold transition-colors ${isRegister && !codeMode ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>Sign up</button>
                  <button data-testid="email-mode-signin" onClick={() => { setIsRegister(false); setCodeMode(false); }} role="tab" aria-selected={!isRegister && !codeMode} className={`flex-1 py-2 min-h-[36px] rounded-lg font-semibold transition-colors ${!isRegister && !codeMode ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>Sign in</button>
                  <button data-testid="email-mode-code" onClick={() => setCodeMode(true)} role="tab" aria-selected={codeMode} className={`flex-1 py-2 min-h-[36px] rounded-lg font-semibold transition-colors ${codeMode ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>Email code</button>
                </div>
                {codeMode ? (
                  <EmailCodeSignIn testid="signin-code" cta="Verify and sign in" onDone={async () => { await checkAuth(); navigate("/dashboard"); }} />
                ) : (<>
                {isRegister && (
                  <div><label htmlFor="email-name" className="gi-label">Name</label><input id="email-name" autoComplete="name" data-testid="email-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className="gi-input" /></div>
                )}
                <div><label htmlFor="email-email" className="gi-label">Email</label><input id="email-email" autoComplete="email" data-testid="email-email-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@email.com" className="gi-input" /></div>
                <div><label htmlFor="email-password" className="gi-label">Password</label><input id="email-password" autoComplete={isRegister ? "new-password" : "current-password"} data-testid="email-password-input" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && submitEmail()} placeholder={isRegister ? "At least 8 characters" : "Your password"} className="gi-input" /></div>
                <button data-testid="email-submit-button" onClick={submitEmail} disabled={submitting} className="w-full gi-btn gi-btn-primary">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {isRegister ? "Create account" : "Sign in"}
                </button>
                </>)}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-faint">
            <ShieldCheck className="w-4 h-4 text-kesh" aria-hidden="true" /> Core tools free forever · no credit card
          </div>
          <p className="mt-4 text-center text-xs text-faint">
            By continuing you agree to our{" "}
            <a href="/terms" className="text-brand hover:underline">Terms</a>,{" "}
            <a href="/privacy" className="text-brand hover:underline">Privacy Policy</a> and{" "}
            <a href="/pdpa" className="text-brand hover:underline">PDPA Statement</a>.
          </p>
        </div>
      </main>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
function LinkedInGlyph() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" /></svg>;
}
